import { captureException, addExceptionStep } from '@/lib/errorTracking';
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import {
  handleGetSharingGeometriesGeometryIdSharingGetOptions,
  handleGetSharingGeometriesGeometryIdSharingGetQueryKey,
  handleGetSharingStudiesStudyIdSharingGetOptions,
  handleGetSharingStudiesStudyIdSharingGetQueryKey,
  handleUpdateSharingGeometriesGeometryIdSharingPutMutation,
  handleUpdateSharingStudiesStudyIdSharingPutMutation,
  handleCreateInviteGeometriesGeometryIdSharingInvitesPostMutation,
  handleCreateInviteStudiesStudyIdSharingInvitesPostMutation,
  handleUpdateInviteGeometriesGeometryIdSharingInvitesInviteEmailPatchMutation,
  handleUpdateInviteStudiesStudyIdSharingInvitesInviteEmailPatchMutation,
  handleDeleteInviteGeometriesGeometryIdSharingInvitesInviteEmailDeleteMutation,
  handleDeleteInviteStudiesStudyIdSharingInvitesInviteEmailDeleteMutation,
  handleGetGeometryGeometriesGeometryIdGetQueryKey,
  handleGetStudyStudiesStudyIdGetQueryKey,
} from '../api/generated/@tanstack/react-query.gen';
import type {
  ShareRole,
  SharingInviteResponse,
  SharingSettingsResponse,
  Visibility,
} from '../api/generated/types.gen';
import { useStore } from '../stores/useStore';

export type SharingResourceType = 'geometry' | 'study';

export interface UseSharingResult {
  settings: SharingSettingsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Update the General access scope (visibility + link role). */
  setScope: (visibility: Visibility, linkRole: ShareRole) => void;
  isScopePending: boolean;
  /** Invite a person by email at a given role. */
  sendInvite: (email: string, role: ShareRole) => void;
  isInvitePending: boolean;
  inviteError: unknown;
  lastInviteResult: SharingInviteResponse | undefined;
  /** Change an existing invitee's role. */
  changeInviteRole: (email: string, role: ShareRole) => void;
  /** Remove a person's access entirely. */
  deleteInvite: (email: string) => void;
}

/**
 * Sharing settings + mutations for a geometry or a study, backing
 * `ShareDialog` for both resource types.
 *
 * React's rules of hooks forbid calling `useQuery`/`useMutation`
 * conditionally, so this can't branch on `resourceType` before calling the
 * generated hooks — instead it calls BOTH the geometry and study hook sets
 * on every render (each `enabled: false`/inert for the inactive type) and
 * returns only the active branch's result. This keeps the branch-selection
 * logic in one small map (`ACTIVE`) rather than duplicating call sites, while
 * staying rules-of-hooks-safe.
 */
export function useSharing(
  resourceType: SharingResourceType,
  id: string,
  /**
   * Gates the settings fetch. `ShareDialog` is mounted unconditionally by
   * `GeometryVisibilityControl`/`StudyVisibilityControl` (so its open/close
   * transition animates), passing its own `open` state here — without this,
   * every render of the control would fire `GET /.../sharing` for every
   * viewer, owner or not, whether or not they ever open the dialog.
   */
  enabled = true,
): UseSharingResult {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const showToast = useStore((s) => s.showToast);

  const isGeometry = resourceType === 'geometry';
  const isStudy = resourceType === 'study';

  const geometryPath = { geometry_id: id };
  const studyPath = { study_id: id };

  // --- Settings query: one per resource type, only one enabled. ---
  const geometrySettings = useQuery({
    ...handleGetSharingGeometriesGeometryIdSharingGetOptions({
      path: geometryPath,
    }),
    enabled: isGeometry && !!id && enabled,
  });
  const studySettings = useQuery({
    ...handleGetSharingStudiesStudyIdSharingGetOptions({ path: studyPath }),
    enabled: isStudy && !!id && enabled,
  });

  const onMutationError = useCallback(
    (action: string) => (error: unknown) => {
      captureException(error, { resourceType, id, action });
      showToast(t('compCommon.toast.sharingUpdateFailed'));
    },
    [resourceType, id, showToast, t],
  );

  const invalidateGeometry = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: handleGetSharingGeometriesGeometryIdSharingGetQueryKey({
        path: geometryPath,
      }),
    });
    void queryClient.invalidateQueries({
      queryKey: handleGetGeometryGeometriesGeometryIdGetQueryKey({
        path: geometryPath,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, id]);

  const invalidateStudy = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: handleGetSharingStudiesStudyIdSharingGetQueryKey({
        path: studyPath,
      }),
    });
    void queryClient.invalidateQueries({
      queryKey: handleGetStudyStudiesStudyIdGetQueryKey({ path: studyPath }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, id]);

  // --- Update scope (visibility + link role). ---
  // One success handler for both resource kinds, like `onMutationError`: only
  // the cache to invalidate differs. `resourceType` is the hook's own, and
  // only the matching mutation is ever fired.
  const onScopeUpdated = useCallback(
    (invalidate: () => void) =>
      (data: { visibility: string; link_role: string }) => {
        invalidate();
        posthog.capture('sharing_scope_updated', {
          resource_type: resourceType,
          resource_id: id,
          visibility: data.visibility,
          link_role: data.link_role,
        });
        addExceptionStep(
          `Scope updated to ${data.visibility}/${data.link_role}: ${id}`,
          { category: 'sharing' },
        );
      },
    [posthog, resourceType, id],
  );
  const geometryScopeMutation = useMutation({
    ...handleUpdateSharingGeometriesGeometryIdSharingPutMutation(),
    onSuccess: onScopeUpdated(invalidateGeometry),
    onError: onMutationError('update_scope'),
  });
  const studyScopeMutation = useMutation({
    ...handleUpdateSharingStudiesStudyIdSharingPutMutation(),
    onSuccess: onScopeUpdated(invalidateStudy),
    onError: onMutationError('update_scope'),
  });

  // --- Invite a person. ---
  const geometryInviteMutation = useMutation({
    ...handleCreateInviteGeometriesGeometryIdSharingInvitesPostMutation(),
    onSuccess: (data) => {
      invalidateGeometry();
      posthog.capture('sharing_invite_created', {
        resource_type: 'geometry',
        resource_id: id,
        role: data.share.role,
        email_sent: data.email_sent,
      });
    },
    onError: onMutationError('invite'),
  });
  const studyInviteMutation = useMutation({
    ...handleCreateInviteStudiesStudyIdSharingInvitesPostMutation(),
    onSuccess: (data) => {
      invalidateStudy();
      posthog.capture('sharing_invite_created', {
        resource_type: 'study',
        resource_id: id,
        role: data.share.role,
        email_sent: data.email_sent,
      });
    },
    onError: onMutationError('invite'),
  });

  // --- Change an existing invitee's role. ---
  const geometryUpdateInviteRoleMutation = useMutation({
    ...handleUpdateInviteGeometriesGeometryIdSharingInvitesInviteEmailPatchMutation(),
    onSuccess: () => {
      invalidateGeometry();
      posthog.capture('sharing_invite_role_updated', {
        resource_type: 'geometry',
        resource_id: id,
      });
    },
    onError: onMutationError('update_invite_role'),
  });
  const studyUpdateInviteRoleMutation = useMutation({
    ...handleUpdateInviteStudiesStudyIdSharingInvitesInviteEmailPatchMutation(),
    onSuccess: () => {
      invalidateStudy();
      posthog.capture('sharing_invite_role_updated', {
        resource_type: 'study',
        resource_id: id,
      });
    },
    onError: onMutationError('update_invite_role'),
  });

  // --- Remove a person's access. ---
  const geometryRemoveInviteMutation = useMutation({
    ...handleDeleteInviteGeometriesGeometryIdSharingInvitesInviteEmailDeleteMutation(),
    onSuccess: () => {
      invalidateGeometry();
      posthog.capture('sharing_invite_removed', {
        resource_type: 'geometry',
        resource_id: id,
      });
    },
    onError: onMutationError('remove_invite'),
  });
  const studyRemoveInviteMutation = useMutation({
    ...handleDeleteInviteStudiesStudyIdSharingInvitesInviteEmailDeleteMutation(),
    onSuccess: () => {
      invalidateStudy();
      posthog.capture('sharing_invite_removed', {
        resource_type: 'study',
        resource_id: id,
      });
    },
    onError: onMutationError('remove_invite'),
  });

  const setScope = useCallback(
    (visibility: Visibility, linkRole: ShareRole) => {
      const body = { visibility, link_role: linkRole };
      if (isGeometry) {
        geometryScopeMutation.mutate({ path: geometryPath, body });
      } else {
        studyScopeMutation.mutate({ path: studyPath, body });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isGeometry, id, geometryScopeMutation, studyScopeMutation],
  );

  const sendInvite = useCallback(
    (email: string, role: ShareRole) => {
      const body = { email, role };
      if (isGeometry) {
        geometryInviteMutation.mutate({ path: geometryPath, body });
      } else {
        studyInviteMutation.mutate({ path: studyPath, body });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isGeometry, id, geometryInviteMutation, studyInviteMutation],
  );

  const changeInviteRole = useCallback(
    (email: string, role: ShareRole) => {
      if (isGeometry) {
        geometryUpdateInviteRoleMutation.mutate({
          path: { ...geometryPath, invite_email: email },
          body: { role },
        });
      } else {
        studyUpdateInviteRoleMutation.mutate({
          path: { ...studyPath, invite_email: email },
          body: { role },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isGeometry,
      id,
      geometryUpdateInviteRoleMutation,
      studyUpdateInviteRoleMutation,
    ],
  );

  const deleteInvite = useCallback(
    (email: string) => {
      if (isGeometry) {
        geometryRemoveInviteMutation.mutate({
          path: { ...geometryPath, invite_email: email },
        });
      } else {
        studyRemoveInviteMutation.mutate({
          path: { ...studyPath, invite_email: email },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isGeometry, id, geometryRemoveInviteMutation, studyRemoveInviteMutation],
  );

  const settingsQuery = isGeometry ? geometrySettings : studySettings;
  const scopeMutation = isGeometry ? geometryScopeMutation : studyScopeMutation;
  const inviteMutation = isGeometry
    ? geometryInviteMutation
    : studyInviteMutation;

  return {
    settings: settingsQuery.data,
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    setScope,
    isScopePending: scopeMutation.isPending,
    sendInvite,
    isInvitePending: inviteMutation.isPending,
    inviteError: inviteMutation.error,
    lastInviteResult: inviteMutation.data,
    changeInviteRole,
    deleteInvite,
  };
}
